import { RefObject, useEffect } from "react";

interface UseInputFocusOptions {
  delay?: number;
  shouldFocus?: boolean;
}

/**
 * Custom hook for automatically focusing an Ionic input element.
 *
 * This hook provides a convenient way to focus an input field with a configurable delay.
 * It's particularly useful for focusing inputs when modals open or when navigating to forms.
 *
 * @param inputRef - Reference to the Ionic input element to focus
 * @param options - Configuration options for the focus behavior
 * @param options.delay - Delay in milliseconds before focusing the input
 * @param options.shouldFocus - Whether the input should be focused
 */
const useInputFocus = (
  inputRef: RefObject<HTMLIonInputElement>,
  options: UseInputFocusOptions = {},
) => {
  const { delay = 240, shouldFocus = true } = options;

  useEffect(() => {
    if (!shouldFocus) return;

    const timer = setTimeout(() => {
      inputRef.current?.setFocus();
    }, delay);

    return () => clearTimeout(timer);
  }, [inputRef, delay, shouldFocus]);
};

export default useInputFocus;
